import { useEffect, useMemo } from 'react';
import { createStore, useAtom, useAtomValue, useStore } from 'jotai';
import { commonSnackbarPropsAtom } from '../common/state/state';
import { contractsAtom, currentContextAtom, globalAuthorizationAtom, userAtom } from '../state';
import { currentBuyingCompanyIdAtom } from '../basket/state/state';
import { brandsAtom } from '../brands/state';
import { wishlistItemsAtom, wishlistSupplierAtom } from '../wishlist/state';

export default function useBaseStore(): ReturnType<typeof useStore> {
  const context = useAtomValue(currentContextAtom);
  const user = useAtomValue(userAtom);
  const brands = useAtomValue(brandsAtom);
  const contracts = useAtomValue(contractsAtom);
  const globalAuthorization = useAtomValue(globalAuthorizationAtom);
  const currentBuyingCompanyId = useAtomValue(currentBuyingCompanyIdAtom);
  const [commonSnackbarProps, setCommonSnackbarProps] = useAtom(commonSnackbarPropsAtom);
  const [wishlistItems, setWishlistItems] = useAtom(wishlistItemsAtom);
  const [wishlistSuppliers, setWishlistSuppliers] = useAtom(wishlistSupplierAtom);
  const baseStore = useMemo(() => {
    const store = createStore();

    store.set(commonSnackbarPropsAtom, commonSnackbarProps);
    store.set(currentContextAtom, context);
    store.set(userAtom, user);
    store.set(brandsAtom, brands);
    store.set(contractsAtom, contracts);
    store.set(globalAuthorizationAtom, globalAuthorization);
    store.set(wishlistItemsAtom, wishlistItems);
    store.set(wishlistSupplierAtom, wishlistSuppliers);
    store.set(currentBuyingCompanyIdAtom, currentBuyingCompanyId);
    store.sub(commonSnackbarPropsAtom, () => {
      setCommonSnackbarProps(store.get(commonSnackbarPropsAtom));
    });
    store.sub(wishlistItemsAtom, () => {
      setWishlistItems(store.get(wishlistItemsAtom));
    });
    store.sub(wishlistSupplierAtom, () => {
      setWishlistSuppliers(store.get(wishlistSupplierAtom));
    });

    return store;
  }, []);

  useEffect(() => {
    baseStore.set(currentContextAtom, context);
    baseStore.set(userAtom, user);
    baseStore.set(currentBuyingCompanyIdAtom, currentBuyingCompanyId);
  }, [user, context, currentBuyingCompanyId]);

  useEffect(() => {
    baseStore.set(contractsAtom, contracts);
  }, [contracts]);

  useEffect(() => {
    baseStore.set(globalAuthorizationAtom, globalAuthorization);
  }, [globalAuthorization]);

  useEffect(() => {
    baseStore.set(wishlistItemsAtom, wishlistItems);
  }, [wishlistItems]);

  useEffect(() => {
    baseStore.set(wishlistSupplierAtom, wishlistSuppliers);
  }, [wishlistSuppliers]);

  useEffect(() => {
    baseStore.set(commonSnackbarPropsAtom, commonSnackbarProps);
  }, [commonSnackbarProps]);

  return baseStore;
}
